import { useMemo, useState } from "react";
import type { RemoteAgentProtocol } from "@shared/domain";
import type { RemoteAgentConnectionTest } from "@shared/remoteAgents";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { Sheet } from "@/components/ui/Sheet";
import { useCreateRemoteAgentMutation, useTestRemoteAgentMutation } from "@/hooks/useBackendData";

interface Props {
  open: boolean;
  onClose: () => void;
}

type ProtocolInput = "auto" | RemoteAgentProtocol;

export const AddAgentSheet = ({ open, onClose }: Props): JSX.Element | null => {
  const testMutation = useTestRemoteAgentMutation();
  const createMutation = useCreateRemoteAgentMutation();
  const [name, setName] = useState("Birbal");
  const [baseUrl, setBaseUrl] = useState("https://birbal.hosttune.com");
  const [protocol, setProtocol] = useState<ProtocolInput>("auto");
  const [apiKey, setApiKey] = useState("");
  const [useCloudflareAccess, setUseCloudflareAccess] = useState(true);
  const [cloudflareAccessClientId, setCloudflareAccessClientId] = useState("");
  const [cloudflareAccessClientSecret, setCloudflareAccessClientSecret] = useState("");
  const [model, setModel] = useState("");
  const [tested, setTested] = useState<RemoteAgentConnectionTest>();

  const credentialsValid = useMemo(
    () =>
      apiKey.trim().length > 0 &&
      (!useCloudflareAccess ||
        (cloudflareAccessClientId.trim().length > 0 && cloudflareAccessClientSecret.trim().length > 0)),
    [apiKey, cloudflareAccessClientId, cloudflareAccessClientSecret, useCloudflareAccess]
  );
  const canTest = baseUrl.trim().length > 0 && credentialsValid && !testMutation.isPending;
  const testError = testMutation.isError
    ? testMutation.error instanceof Error
      ? testMutation.error.message
      : "Connection test failed."
    : undefined;

  const markConnectionDirty = () => {
    setTested(undefined);
    testMutation.reset();
  };

  const connectionInput = () => ({
    baseUrl: baseUrl.trim(),
    protocol,
    model: model || undefined,
    credentials: {
      apiKey: apiKey.trim(),
      cloudflareAccessClientId: useCloudflareAccess ? cloudflareAccessClientId.trim() : undefined,
      cloudflareAccessClientSecret: useCloudflareAccess ? cloudflareAccessClientSecret.trim() : undefined
    }
  });

  const reset = () => {
    setName("Birbal");
    setBaseUrl("https://birbal.hosttune.com");
    setProtocol("auto");
    setApiKey("");
    setUseCloudflareAccess(true);
    setCloudflareAccessClientId("");
    setCloudflareAccessClientSecret("");
    setModel("");
    setTested(undefined);
    testMutation.reset();
  };

  if (!open) return null;

  return (
    <Sheet open={open} title="Add Remote Agent" onClose={onClose} height="80">
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
          Connect a Hermes Runs or OpenAI-compatible agent. Credentials are encrypted and are never shown after saving.
        </p>

        <div className="space-y-3 border-b border-[var(--border-subtle)] pb-4">
          <InputField
            label="Agent name"
            inputProps={{
              value: name,
              maxLength: 80,
              placeholder: "Birbal",
              onChange: (event) => setName(event.target.value)
            }}
          />
          <InputField
            label="Base URL"
            inputProps={{
              value: baseUrl,
              inputMode: "url",
              placeholder: "https://agent.example.com",
              onChange: (event) => {
                setBaseUrl(event.target.value);
                markConnectionDirty();
              }
            }}
          />
          <label className="block text-sm text-[var(--text-secondary)]">
            Protocol
            <select
              className="input mt-1"
              value={protocol}
              onChange={(event) => {
                setProtocol(event.target.value as ProtocolInput);
                markConnectionDirty();
              }}
            >
              <option value="auto">Auto-detect</option>
              <option value="hermes_runs">Hermes Runs</option>
              <option value="openai_chat">OpenAI-compatible</option>
            </select>
          </label>
        </div>

        <div className="space-y-3">
          <InputField
            label="API bearer key"
            inputProps={{
              type: "password",
              value: apiKey,
              autoComplete: "new-password",
              onChange: (event) => {
                setApiKey(event.target.value);
                markConnectionDirty();
              }
            }}
          />
          <label className="flex min-h-11 items-center gap-3 text-sm text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={useCloudflareAccess}
              onChange={(event) => {
                setUseCloudflareAccess(event.target.checked);
                markConnectionDirty();
              }}
            />
            Protected by Cloudflare Access
          </label>
          {useCloudflareAccess ? (
            <div className="space-y-3 border-l-2 border-[var(--accent-primary)] pl-3">
              <InputField
                label="Access client ID"
                inputProps={{
                  value: cloudflareAccessClientId,
                  autoComplete: "off",
                  onChange: (event) => {
                    setCloudflareAccessClientId(event.target.value);
                    markConnectionDirty();
                  }
                }}
              />
              <InputField
                label="Access client secret"
                inputProps={{
                  type: "password",
                  value: cloudflareAccessClientSecret,
                  autoComplete: "new-password",
                  onChange: (event) => {
                    setCloudflareAccessClientSecret(event.target.value);
                    markConnectionDirty();
                  }
                }}
              />
            </div>
          ) : null}
        </div>

        <Button
          className="w-full"
          variant="secondary"
          disabled={!canTest}
          onClick={async () => {
            try {
              const result = await testMutation.mutateAsync(connectionInput());
              setTested(result);
              setProtocol(result.protocol);
              setBaseUrl(result.baseUrl);
              setModel(result.selectedModel);
            } catch {
              setTested(undefined);
            }
          }}
        >
          {testMutation.isPending ? "Testing connection…" : "Test Connection"}
        </Button>

        <div aria-live="polite">
          {testError ? <p className="text-sm text-[var(--status-blocker)]">{testError}</p> : null}
          {tested ? (
            <div className="space-y-2 rounded-xl bg-[var(--status-success-bg)] p-3 text-sm text-[var(--status-success)]">
              <p className="font-semibold">Connection verified</p>
              <p>
                {tested.protocol === "hermes_runs" ? "Hermes Runs" : "OpenAI-compatible"} · {tested.models.length} model
                {tested.models.length === 1 ? "" : "s"} discovered
              </p>
            </div>
          ) : null}
        </div>

        {tested ? (
          <label className="block text-sm text-[var(--text-secondary)]">
            Model
            <select className="input mt-1" value={model} onChange={(event) => setModel(event.target.value)}>
              {tested.models.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <Button
          className="w-full"
          disabled={!tested || !model || name.trim().length < 2 || createMutation.isPending}
          onClick={async () => {
            try {
              await createMutation.mutateAsync({ ...connectionInput(), name: name.trim(), model });
              reset();
              onClose();
            } catch {
              // toast emitted by mutation
            }
          }}
        >
          {createMutation.isPending ? "Adding agent…" : "Add Agent"}
        </Button>
      </div>
    </Sheet>
  );
};
